import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useIsFetching } from "@tanstack/react-query";

const LoadingContext = createContext();

export const useLoading = () => useContext(LoadingContext);

export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(true);
  
  const location = useLocation();
  const isFetching = useIsFetching();
  const prevPathRef = useRef(location.pathname);
  const startTimeRef = useRef(0);
  
  // Fonction pour forcer le loader manuellement avec réinitialisation du timer
  const startManualLoading = (msg = "Chargement en cours...") => {
    setMessage(msg);
    startTimeRef.current = Date.now();
    setIsLoading(true);
  };
  
  // Dictionnaire des messages par route
  const getMessageForRoute = (pathname) => {
    if (pathname.includes("/dashboard")) return "Initialisation du tableau de bord...";
    if (pathname.includes("/prospects/leads")) return "Synchronisation de vos leads...";
    if (pathname.includes("/prospects/familles")) return "Chargement des familles...";
    if (pathname.includes("/prospects")) return "Récupération de la base CRM...";
    if (pathname.includes("/produits")) return "Chargement du catalogue produits...";
    if (pathname.includes("/partenaires")) return "Synchronisation des partenaires...";
    if (pathname.includes("/forum")) return "Connexion au forum...";
    if (pathname.includes("/conseil/audits")) return "Génération des audits...";
    if (pathname.includes("/conseil/allocations")) return "Chargement des allocations...";
    if (pathname.includes("/outils/quantalys")) return "Connexion à Quantalys...";
    if (pathname.includes("/outils/impots")) return "Initialisation du simulateur fiscal...";
    if (pathname.includes("/transactions")) return "Récupération des flux financiers...";
    if (pathname.includes("/documents")) return "Accès à la GED...";
    if (pathname === "/login") return "Connexion sécurisée...";
    return "Chargement en cours...";
  };

  useLayoutEffect(() => {
    const currentPath = location.pathname;
    
    // Détermination du Fullscreen
    // - Sur le Login
    // - Vers le Dashboard (soit depuis Login, soit clic Sidebar)
    const isLogin = currentPath === "/login";
    const isDashboard = currentPath.includes("/dashboard");
    const isFullScreenNow = isLogin || isDashboard;
    
    setIsFullScreen(isFullScreenNow);
    setMessage(getMessageForRoute(currentPath));
    startTimeRef.current = Date.now();
    setIsLoading(true);

    // On laisse le temps à React Query de déclencher ses requêtes (environ 150ms)
    // Si après ce délai, il n'y a aucune requête en cours, on coupe le loader (page en cache ou sans data).
    const checkTimer = setTimeout(() => {
      if (isFetching === 0) {
          const elapsed = Date.now() - startTimeRef.current;
          const remaining = Math.max(0, 2000 - elapsed);
          setTimeout(() => setIsLoading(false), remaining);
      }
    }, 150); 

    // Sécurité globale : ne jamais rester bloqué plus de 10 secondes sur une navigation
    const safetyTimer = setTimeout(() => {
       setIsLoading(false);
    }, 10000);

    prevPathRef.current = currentPath;

    return () => {
      clearTimeout(checkTimer);
      clearTimeout(safetyTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Uniquement déclenché au changement de route

  // Coupe le loader dès que React Query a terminé de charger les données
  useEffect(() => {
    if (isLoading && isFetching === 0) {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, 2000 - elapsed);
      
      const t = setTimeout(() => setIsLoading(false), remaining);
      return () => clearTimeout(t);
    }
  }, [isFetching, isLoading]);

  return (
    <LoadingContext.Provider value={{ isLoading, message, isFullScreen, setIsLoading, setMessage, startManualLoading }}>
      {children}
    </LoadingContext.Provider>
  );
};
